import React from "react";
import get from "lodash/get";
import { BarChart, Bar, ResponsiveContainer, XAxis, YAxis, CartesianGrid, Tooltip } from "recharts";

// components
import Collapse, { ICollapse } from "@/components/Collapse";
import FootfallIcon from "@/components/icons/FootfallIcon";

// helpers
import separatedNumber from "@/helpers/separatedNumber";

// assets
import styles from "@/assets/styles/containers/location-space-demographic-chart.module.scss";


export interface IWeeklyFootfall extends ICollapse {
  totalFootFall?: number;
  dataSource?: Array<{ label: string; value: number }>;
  isTabletView?: boolean;
  isCollapsing?: boolean;
}

const CUSTOM_TICKS = Array.from({ length: 11 }, (_, index) => index * 10);

function WeeklyFootfall({ totalFootFall, dataSource, isTabletView, isCollapsing, ...rest }: IWeeklyFootfall) {

  const maxValueDraft = Math.max(...(dataSource || []).map((item) => item.value));
  const maxValue = Math.ceil(maxValueDraft / 10) * 10;

  return (
    <Collapse
      className={`${styles["locl-location-space-demographic-chart"]} ${styles["locl-location-space-demographic-chart-footfall"]}`}
      defaultActiveKey={["1"]}
      collapsible={isCollapsing ? "header" : "disabled"}
      key={`WeeklyFootfall-${totalFootFall}`}
      {...rest}
    >
      <Collapse.Panel
        header={
          <div className={styles["locl-location-space-demographic-chart__header"]} data-testid="locl-location-statistics-card-footfall">
            <div className={styles["locl-location-space-demographic-chart__header__label"]}>
              Weekly footfall
            </div>
            <div className={styles["locl-location-space-demographic-chart__header__content"]}>
              <div className={styles["locl-location-space-demographic-chart__header__icon"]}>
                <FootfallIcon />
              </div>
              <div className={styles["locl-location-space-demographic-chart__header__number"]} data-testid="locl-statistic-card-value">
                {separatedNumber(totalFootFall) || null}
              </div>
            </div>
          </div>
        }
        key="1"
        showArrow
        data-testid={get(rest, "data-testid")}
      >
        <div className={styles["locl-location-space-demographic-chart__body"]} data-testid="locl-location-space-demographic-chart-body">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart layout="vertical" data={dataSource} barSize={20}>
              <YAxis
                axisLine={false}
                tickLine={false}
                type="category"
                width={60}
                dataKey="label"
                tickMargin={10}
              />
              <XAxis
                axisLine={false}
                tickLine={false}
                type="number"
                xAxisId="bottom"
                tickFormatter={(e) => `${e}%`}
                domain={[0, maxValue]}
                ticks={isTabletView ? [0, maxValue] : CUSTOM_TICKS.filter(item => item <= maxValue)}
              />
              <XAxis height={40} hide type="number" domain={[0, maxValue]} ticks={CUSTOM_TICKS.filter(item => item <= maxValue)} />
              <Tooltip
                cursor={false}
                allowEscapeViewBox={{ x: true, y: true }}
                content={props => (
                  <span className={styles["locl-location-space-demographic-chart__body__tooltip"]}>
                    {get(props, "payload[0].value")}%
                  </span>
                )}
              />
              <Bar
                dataKey="value"
                fill="var(--primary-color-500)"
                background={{ fill: "var(--secondary-color-300)" }}
                tooltipType="none"
              />
              <CartesianGrid
                stroke="var(--secondary-color-400)"
                strokeDasharray="1 0"
                horizontalCoordinatesGenerator={() => []}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Collapse.Panel>
    </Collapse>
  );
}

export default WeeklyFootfall;
